import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useChat } from './ChatContext';
import { ModelSelection } from '../types/models';

export interface NavigationState {
  // Current selections
  selectedPinnedArtifactId: string | null;
  selectedThreadId: string | null;
  selectedFocusTaskId: string | null;
  selectedCenterCardId: string | null;
  
  // UI state
  isRightPanelOpen: boolean;
  rightPanelContent: 'artifact-details' | 'thread-details' | 'task-details' | null;
  
  // Context for new threads
  newThreadContext: {
    type: 'pinned-artifact' | 'focus-task' | null;
    itemId: string | null;
    uiRole?: string;
    environment?: string;
  };
}

export interface NavigationActions {
  // Pinned artifact navigation
  selectPinnedArtifact: (artifactId: string, uiRole?: string, environment?: string) => Promise<void>;
  
  // Thread navigation
  selectThread: (threadId: string) => Promise<void>;
  
  // Focus task navigation
  selectFocusTask: (taskId: string, uiRole?: string, environment?: string) => Promise<void>;
  
  // Center card navigation
  selectCenterCard: (cardId: string, cardType: 'artifact' | 'thread' | 'task') => void;
  
  // Thread creation with context
  createThreadFromContext: (name: string, model: ModelSelection, uiRole: string, context?: any) => Promise<void>;
  
  // Clear new thread context
  clearNewThreadContext: () => void;
  
  // Right panel management
  openRightPanel: (content: 'artifact-details' | 'thread-details' | 'task-details') => void;
  closeRightPanel: () => void;
  
  // Clear selections
  clearSelections: () => void;
}

export interface NavigationContextType extends NavigationState, NavigationActions {}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const { createThread, selectThread: chatSelectThread } = useChat();
  
  const [state, setState] = useState<NavigationState>({
    selectedPinnedArtifactId: null,
    selectedThreadId: null,
    selectedFocusTaskId: null,
    selectedCenterCardId: null,
    isRightPanelOpen: false,
    rightPanelContent: null,
    newThreadContext: {
      type: null,
      itemId: null,
      uiRole: undefined,
      environment: undefined,
    },
  });

  // Pinned artifact selection
  const selectPinnedArtifact = useCallback(async (artifactId: string, uiRole?: string, environment?: string) => {
    setState(prev => ({
      ...prev,
      selectedPinnedArtifactId: artifactId,
      selectedThreadId: null, // Clear thread selection
      selectedFocusTaskId: null, // Clear focus task selection
      selectedCenterCardId: artifactId, // Show the artifact card in center
      isRightPanelOpen: true,
      rightPanelContent: 'artifact-details',
      newThreadContext: {
        type: 'pinned-artifact',
        itemId: artifactId,
        uiRole,
        environment,
      },
    }));
  }, []);

  // Thread selection
  const selectThread = useCallback(async (threadId: string) => {
    setState(prev => ({
      ...prev,
      selectedThreadId: threadId,
      selectedPinnedArtifactId: null, // Clear pinned selection
      selectedFocusTaskId: null, // Clear focus task selection
      selectedCenterCardId: threadId, // Show the thread in center
      isRightPanelOpen: false, // Close right panel for thread view
      rightPanelContent: null,
      newThreadContext: {
        type: null,
        itemId: null,
        uiRole: undefined,
        environment: undefined,
      },
    }));
    
    // Also select the thread in chat context to load its messages
    await chatSelectThread(threadId);
  }, [chatSelectThread]);

  // Focus task selection
  const selectFocusTask = useCallback(async (taskId: string, uiRole?: string, environment?: string) => {
    setState(prev => ({
      ...prev,
      selectedFocusTaskId: taskId,
      selectedPinnedArtifactId: null, // Clear pinned selection
      selectedThreadId: null, // Clear thread selection
      selectedCenterCardId: taskId, // Show the task card in center
      isRightPanelOpen: true,
      rightPanelContent: 'task-details',
      newThreadContext: {
        type: 'focus-task',
        itemId: taskId,
        uiRole,
        environment,
      },
    }));
  }, []);

  // Center card selection
  const selectCenterCard = useCallback((cardId: string, cardType: 'artifact' | 'thread' | 'task') => {
    setState(prev => ({
      ...prev,
      selectedCenterCardId: cardId,
      // Set the appropriate selection based on card type
      selectedPinnedArtifactId: cardType === 'artifact' ? cardId : null,
      selectedThreadId: cardType === 'thread' ? cardId : null,
      selectedFocusTaskId: cardType === 'task' ? cardId : null,
      isRightPanelOpen: true,
      rightPanelContent: cardType === 'artifact' ? 'artifact-details' : 
                        cardType === 'thread' ? 'thread-details' : 
                        'task-details',
      newThreadContext: {
        type: null,
        itemId: null,
        uiRole: undefined,
        environment: undefined,
      },
    }));
  }, []);

  // Thread creation with context
  const createThreadFromContext = useCallback(async (
    name: string, 
    model: ModelSelection, 
    uiRole: string, 
    context?: any
  ) => {
    try {
      const newThread = await createThread(name, undefined, model, uiRole, context);
      
      setState(prev => ({
        ...prev,
        selectedThreadId: newThread.id,
        selectedPinnedArtifactId: null,
        selectedFocusTaskId: null,
        selectedCenterCardId: newThread.id,
        isRightPanelOpen: false,
        rightPanelContent: null,
        newThreadContext: {
          type: null,
          itemId: null,
          uiRole: undefined,
          environment: undefined,
        },
      }));
      
      // Switch to the new thread in chat context
      await chatSelectThread(newThread.id);
    } catch (error) {
      console.error('Failed to create thread from context:', error);
    }
  }, [createThread, chatSelectThread]);

  // Clear new thread context
  const clearNewThreadContext = useCallback(() => {
    setState(prev => ({
      ...prev,
      newThreadContext: {
        type: null,
        itemId: null,
        uiRole: undefined,
        environment: undefined,
      },
    }));
  }, []);

  // Right panel management
  const openRightPanel = useCallback((content: 'artifact-details' | 'thread-details' | 'task-details') => {
    setState(prev => ({
      ...prev,
      isRightPanelOpen: true,
      rightPanelContent: content,
    }));
  }, []);

  const closeRightPanel = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRightPanelOpen: false,
      rightPanelContent: null,
    }));
  }, []);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedPinnedArtifactId: null,
      selectedThreadId: null,
      selectedFocusTaskId: null,
      selectedCenterCardId: null,
      isRightPanelOpen: false,
      rightPanelContent: null,
      newThreadContext: {
        type: null,
        itemId: null,
        uiRole: undefined,
        environment: undefined,
      },
    }));
  }, []);

  const contextValue: NavigationContextType = {
    ...state,
    selectPinnedArtifact,
    selectThread,
    selectFocusTask,
    selectCenterCard,
    createThreadFromContext,
    clearNewThreadContext,
    openRightPanel,
    closeRightPanel,
    clearSelections,
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
